import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';
import hideVisually from 'polished/lib/mixins/hideVisually';
import { doubleFocusRing } from '../utils/focus_ring';

type StylesType = {
  root: {
    base: SerializedStyles;
    hc: {
      base: SerializedStyles;
      checked: SerializedStyles;
      unchecked: SerializedStyles;
    };

    checked: SerializedStyles;
    unchecked: SerializedStyles;

    disabled: SerializedStyles;
  };
  decorative: {
    base: SerializedStyles;
    noIcon: {
      base: SerializedStyles;
      disabled: SerializedStyles;
      checked: {
        base: SerializedStyles;
        disabled: SerializedStyles;
      };
    };
  };
  label: SerializedStyles;
  icon: {
    base: SerializedStyles;
    show: SerializedStyles;
  };
  interactive: SerializedStyles;
};

const useStyles = (): StylesType => {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      root: {
        base: css({
          //to achieve 24px target size on HD
          padding: `calc(calc(24px - ${theme.orbitalSix}) / 2 )`,
          margin: `calc(calc(${theme.orbitalSix} - 24px) / 2 )`,

          gap: theme.orbitalThree,
          color: theme.color.foreground.default,
          cursor: 'pointer',
          display: 'inline-flex',
          wordBreak: 'break-word',
          position: 'relative',
        }),

        disabled: css({
          pointerEvents: 'none',
          color: theme.color.foreground.disabled,
          cursor: 'default',
          '> .vn-checkbox__span': {
            backgroundColor: theme.color.background.disabled,
          },
        }),

        checked: css({
          '&:hover > .vn-checkbox__span': {
            backgroundColor: theme.color.primary.base.hover,
            borderColor: theme.color.primary.base.hover,

            '> svg': {
              fill: theme.color.primary.foreground.hover,
            },
          },

          '&:active > .vn-checkbox__span': {
            backgroundColor: theme.color.primary.base.active,
            borderColor: theme.color.primary.base.active,

            '> svg': {
              fill: theme.color.primary.foreground.active,
            },
          },
        }),

        unchecked: css({
          '> .vn-checkbox__span': {
            backgroundColor: theme.color.background.container.default,

            '&:hover': {
              backgroundColor: theme.color.background.container.hover,
              borderColor: theme.color.neutral.base.hover,
            },

            '&:active': {
              backgroundColor: theme.color.background.container.active,
              borderColor: theme.color.neutral.base.active,
            },
          },
        }),

        hc: {
          base: css({
            color: theme.color.neutral.base.default,
            '&:hover': {
              color: theme.color.neutral.base.hover,
            },
            '&:active': {
              color: theme.color.neutral.base.active,
            },
          }),
          checked: css({
            '&:hover > .vn-checkbox__span': {
              backgroundColor: theme.color.primary.base.hover,
              borderColor: theme.color.primary.base.hover,
            },

            '&:active > .vn-checkbox__span': {
              backgroundColor: theme.color.primary.base.active,
              borderColor: theme.color.primary.base.active,
            },
          }),
          unchecked: css({
            '&:hover > .vn-checkbox__span': {
              backgroundColor: theme.color.background.container.default,
              borderColor: theme.color.neutral.base.hover,
            },

            '&:active > .vn-checkbox__span': {
              backgroundColor: theme.color.background.container.default,
              borderColor: theme.color.neutral.base.active,
            },
          }),
        },
      },

      decorative: {
        base: css({
          backgroundColor: 'transparent',
          alignItems: 'inherit',
          borderRadius: theme.borderRadius2,
          boxSizing: 'border-box',
          display: 'inline-flex',
          position: 'relative',
          minWidth: theme.orbitalSix,
          width: theme.orbitalSix,
          minHeight: theme.orbitalSix,
          height: theme.orbitalSix,
          userSelect: 'none',
        }),

        noIcon: {
          base: css({
            borderColor: theme.color.neutral.base.default,
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            borderStyle: 'solid',
            borderWidth: '1px',
          }),
          disabled: css({
            borderColor: theme.color.foreground.disabled,
          }),
          checked: {
            base: css({
              backgroundColor: theme.color.primary.base.default,
              borderColor: theme.color.primary.base.default,
            }),
            disabled: css({
              backgroundColor: theme.color.foreground.disabled,
              borderColor: theme.color.foreground.disabled,
            }),
          },
        },
      },

      label: css({
        transition: 'color 0.2s ease',
        alignContent: 'center',
        ...theme.typography.labelRegular,
      }),

      icon: {
        base: css({
          fill: theme.color.primary.foreground.default,
          height: '100%',
          visibility: 'hidden',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
        }),
        show: css({
          visibility: 'inherit',
        }),
      },

      interactive: css({
        ...hideVisually(),
        zIndex: 1,
        clip: 'unset',
        opacity: 0,
        borderRadius: theme.borderRadius2,
        ...doubleFocusRing({
          theme,
          shadowSize: 2,
          extension: '+ .vn-checkbox__span',
        }),
      }),
    };
  }, [theme]);
};

export default useStyles;
